// El selector de Inteligencia de la barra del Chat —
// ADR-2026-09-08-el-chat-toma-el-motor-del-asistente.
//
// Los mismos tres niveles del asistente (`INTEL_OPTIONS`): Baja = Gemini Flash, gratis;
// Media = GPT-6 Sol y Alta = Opus, que consumen créditos por turno. Es un botón con el nivel
// a la vista y un popover hacia ARRIBA (la barra vive al pie), sin el toggle de "Ayuda de
// IA" que tiene el menú del asistente: acá el agente está siempre.
//
// Y por eso mismo tiene el candado por plan que el menú del asistente no necesita: allá
// la "Ayuda de IA" ya pide Pro/Ultra y sin ella la inteligencia no aplica, acá el agente
// está siempre (ADR-2026-09-12-la-inteligencia-del-chat-es-de-pro-para-arriba). Los dos
// niveles pagos se DIBUJAN con candado en vez de esconderse: es la convención de la app
// —lo bloqueado se ve y dice qué plan lo abre— y esconderlos no vende nada.

import { useEffect, useRef, useState } from 'react';
import { Brain, Check, ChevronDown, Lock } from 'lucide-react';
import {
  INTELIGENCIA_BASE,
  INTELLIGENCE_LABEL,
  type Intelligence,
} from '../../pages/v2/agentModels';
import { INTEL_OPTIONS } from '../../pages/v2/modes/asistente/AyudaIAMenu';

interface Props {
  valor: Intelligence;
  onCambiar: (nivel: Intelligence) => void;
  /** ¿El plan deja subir de "Baja"? Con `false`, Media y Alta van con candado. */
  puedeSubir: boolean;
}

export const SelectorInteligencia: React.FC<Props> = ({ valor, onCambiar, puedeSubir }) => {
  const [abierto, setAbierto] = useState(false);
  const raizRef = useRef<HTMLDivElement | null>(null);

  // Click afuera cierra. Escape también, devolviendo el foco al botón.
  useEffect(() => {
    if (!abierto) return;
    const alClick = (ev: MouseEvent | TouchEvent) => {
      if (!raizRef.current?.contains(ev.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', alClick);
    document.addEventListener('touchstart', alClick);
    return () => {
      document.removeEventListener('mousedown', alClick);
      document.removeEventListener('touchstart', alClick);
    };
  }, [abierto]);

  return (
    <div ref={raizRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-label={`Inteligencia: ${INTELLIGENCE_LABEL[valor]}`}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setAbierto(false);
        }}
        className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium transition-colors ${
          abierto
            ? 'bg-[#02062B]/[0.09] text-brand-black'
            : 'bg-[#F0F2F7] text-[#02062B]/[0.7] hover:bg-[#02062B]/[0.09]'
        }`}
      >
        <Brain className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden="true" />
        <span className="hidden sm:inline">Inteligencia</span>
        <span className="text-brand-primary">{INTELLIGENCE_LABEL[valor]}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </button>

      {abierto && (
        <div
          role="listbox"
          aria-label="Nivel de inteligencia"
          className="absolute bottom-full left-0 z-[56] mb-2 w-[264px] rounded-2xl border border-[#02062B]/[0.06] bg-white p-1.5 shadow-xl"
        >
          {/* El título, sólo en mobile: es el espejo exacto del `hidden sm:inline` del
              botón. En desktop el botón ya dice "Inteligencia" justo debajo del popover;
              en mobile lo esconde para que entre en la barra, y la lista Baja/Media/Alta
              quedaba sin decir de qué era. Mismo estilo que el título del selector de
              Modelo de imagen (ADR-2026-09-13-el-popover-de-inteligencia-se-titula-en-mobile). */}
          <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-[#02062B]/[0.45] sm:hidden">
            Inteligencia
          </p>
          {INTEL_OPTIONS.map((opt) => {
            const activo = opt.id === valor;
            // "Baja" nunca se bloquea: es la que no consume créditos y la que queda
            // puesta cuando el plan no alcanza.
            const bloqueado = !puedeSubir && opt.id !== INTELIGENCIA_BASE;
            return (
              <button
                key={opt.id}
                type="button"
                role="option"
                aria-selected={activo}
                // No `disabled`: un `disabled` se saltea en la navegación por teclado y
                // el lector de pantalla no cuenta la opción. Queda anunciada y con el
                // motivo en el `aria-label`.
                aria-disabled={bloqueado}
                aria-label={
                  bloqueado ? `${opt.label} — disponible con los planes Pro y Ultra` : undefined
                }
                onClick={() => {
                  if (bloqueado) return;
                  onCambiar(opt.id);
                  setAbierto(false);
                }}
                className={`flex w-full items-start justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                  bloqueado
                    ? 'cursor-not-allowed opacity-55'
                    : activo
                      ? 'bg-[#1644EB0F]'
                      : 'hover:bg-neutral-50'
                }`}
              >
                <span className="min-w-0">
                  <span
                    className={`block text-[14px] font-medium ${
                      activo && !bloqueado ? 'text-brand-primary' : 'text-[#02062BE6]'
                    }`}
                  >
                    {opt.label}
                  </span>
                  <span className="mt-0.5 block text-[12px] leading-snug text-[#3C3C43]/55">
                    {bloqueado ? 'Disponible con los planes Pro y Ultra' : opt.desc}
                  </span>
                </span>
                {bloqueado ? (
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#02062B]/40" />
                ) : (
                  activo && <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-primary" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SelectorInteligencia;
